import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  CreditCard, Calendar, AlertCircle, ArrowRight, 
  ShieldCheck, ArrowLeft, CheckCircle2, PieChart, Clock
} from 'lucide-react';
import Footer from '@/components/Footer';

const TarjetasMsiInfo = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Volver
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer shadow-sm">
                  Ver Planes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-stone-200/70 border border-stone-300 text-stone-800 px-3.5 py-1.5 rounded-full mb-6 text-xs sm:text-sm font-semibold shadow-xs">
              <CreditCard className="w-4 h-4 text-indigo-600" />
              <span>Control Financiero Total</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
              Domina tus tarjetas y{' '}
              <span className="text-indigo-600">compras a meses sin intereses</span>
            </h1>

            <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed max-w-2xl mx-auto font-medium">
              Las promociones a MSI pueden asfixiar tus ingresos si pierdes la cuenta. Registra cada compra en cuotas, conoce exactamente lo que pagarás cada mes y nunca pagues un centavo de interés.
            </p>

            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm sm:text-base px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Comenzar a Controlar Tarjetas
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-300 bg-white hover:bg-stone-100 text-stone-800 font-bold text-sm sm:text-base px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes y Precios
                </Button>
              </Link>
            </div>
          </div>

          {/* Demonstration Card */}
          <div className="max-w-4xl mx-auto mb-20">
            <Card className="p-6 sm:p-10 bg-white border border-stone-200 shadow-xl rounded-3xl">
              <div className="text-center mb-8">
                <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full">
                  Panel de Tarjeta & MSI
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-stone-900 mt-3 tracking-tight">
                  Visibilidad transparente de tus compras diferidas
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                {/* Tarjeta Simulación */}
                <div className="bg-stone-900 text-white p-6 rounded-2xl shadow-md flex flex-col justify-between min-h-[220px] border border-stone-800">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-[11px] uppercase font-mono tracking-widest text-indigo-400">Tarjeta de Crédito</span>
                      <p className="text-lg font-bold text-white">BBVA Platinum</p>
                    </div>
                    <CreditCard className="w-7 h-7 text-indigo-400" />
                  </div>
                  
                  <div className="my-4">
                    <p className="text-xs text-stone-400">Total Comprometido en MSI</p>
                    <p className="text-2xl sm:text-3xl font-black text-white">$14,990.00 MXN</p>
                  </div>

                  <div className="flex justify-between text-xs pt-3 border-t border-stone-800 text-stone-300">
                    <div>
                      <p className="text-[10px] text-stone-400 uppercase">Corte</p>
                      <p className="font-semibold text-white">Día 12 de cada mes</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-stone-400 uppercase">Límite Pago</p>
                      <p className="font-semibold text-emerald-400">Día 02 del siguiente</p>
                    </div>
                  </div>
                </div>

                {/* Lista de Compras MSI */}
                <div className="space-y-3 flex flex-col justify-center">
                  <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="font-bold text-stone-900 text-sm">Laptop HP Pavilion</span>
                      <span className="text-xs font-bold text-stone-900">$1,250.00/mes</span>
                    </div>
                    <div className="flex justify-between text-xs text-stone-500 mb-1.5 font-medium">
                      <span>Pago 4 de 12 cuotas</span>
                      <span>Resta: $10,000.00</span>
                    </div>
                    <div className="w-full bg-stone-200 h-2 rounded-full overflow-hidden">
                      <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '33%' }}></div>
                    </div>
                  </div>

                  <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="font-bold text-stone-900 text-sm">Vuelos Vacaciones</span>
                      <span className="text-xs font-bold text-stone-900">$833.33/mes</span>
                    </div>
                    <div className="flex justify-between text-xs text-stone-500 mb-1.5 font-medium">
                      <span>Pago 5 de 6 cuotas</span>
                      <span>Resta: $833.33</span>
                    </div>
                    <div className="w-full bg-stone-200 h-2 rounded-full overflow-hidden">
                      <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '83%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Pillars */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20">
            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Mensualidad Exacta</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Calcula automáticamente la suma total de mensualidades que debes cubrir este mes entre todas tus tarjetas.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Clock className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Seguimiento de Cuotas</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Visualiza el avance de cada compra diferida (mes 3 de 12, etc.) y la fecha exacta en la que terminarás de pagarla.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <PieChart className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Realidad Financiera</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Distingue entre el dinero líquido en tu cuenta de débito y el capital comprometido en tus tarjetas de crédito.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <AlertCircle className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Fechas de Corte y Pago</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Guarda los días clave de cada tarjeta para saber el mejor momento para comprar y no retrasarte en el pago para no generar intereses.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Registro de Pagos a Tarjeta</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Registra tus abonos mensuales con un toque. La app descuenta el pago de tu cuenta de origen y actualiza el saldo de tu tarjeta.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Seguridad Absoluta</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Nunca solicitamos contraseñas bancarias, tokens ni CVV. Todo el registro es privado y bajo tu control.
              </p>
            </Card>
          </div>

          {/* CTA Card */}
          <Card className="p-8 sm:p-14 bg-stone-900 text-white text-center rounded-3xl border border-stone-800 shadow-xl">
            <h2 className="text-2xl sm:text-4xl font-black mb-3 tracking-tight">
              Pon en orden tus tarjetas hoy mismo
            </h2>
            <p className="text-sm sm:text-base text-stone-400 mb-8 max-w-xl mx-auto leading-relaxed font-medium">
              Disfruta los beneficios de tus tarjetas y los meses sin intereses sin caer en la trampa del sobreendeudamiento.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Crear Cuenta Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-700 bg-stone-800 hover:bg-stone-700 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes Disponibles
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TarjetasMsiInfo;
